import { useTranslation } from 'react-i18next'
import { TouchableOpacity, View } from 'react-native'
import { Icon, Text, useTheme } from 'react-native-paper'
import Row from '../grid/row'

/**
 * Display a no data message where we can have optional actions of creating or searching
 * @returns
 */
export type NoDataMessageVariant = 'inline' | 'standaloneIllustrated'

export const NoDataMessage = ({
  labels,
  onSearch,
  onCreate,
  variant = 'inline',
}: {
  labels: {
    noDataMessage: string
    createMessage?: string
    searchMessage?: string
  }
  onSearch?: () => void
  onCreate?: () => void
  /**
   * `inline` — small centered helper text (optionally with create/search links).
   * `standaloneIllustrated` — full-width empty state with icon + larger body copy; if links are passed, they render in a row below the message.
   */
  variant?: NoDataMessageVariant
}) => {
  const { t } = useTranslation('common')
  const theme = useTheme()

  const hasActions = !!(labels.createMessage || labels.searchMessage)
  if (variant === 'standaloneIllustrated') {
    const illustration = hasActions ? 'inbox-outline' : 'credit-card-outline'
    return (
      <View
        style={{
          alignSelf: 'stretch',
          alignItems: 'center',
          paddingVertical: 32,
          paddingHorizontal: 24,
        }}
      >
        <Icon
          source={illustration}
          size={52}
          color={theme.colors.onSurfaceDisabled}
        />
        <Text
          variant="bodyLarge"
          style={{
            textAlign: 'center',
            marginTop: 16,
            color: theme.colors.onSurfaceVariant,
          }}
        >
          {labels.noDataMessage}
        </Text>
        {hasActions ? (
          <Row
            alignItems="center"
            columnGap={8}
            wrap
            justifyContent="center"
            style={{
              marginTop: 20,
              rowGap: 8,
              columnGap: 8,
            }}
          >
            {labels.createMessage ? (
              <TouchableOpacity onPress={onCreate}>
                <Text
                  variant="bodyMedium"
                  style={{
                    textDecorationLine: 'underline',
                    color: theme.colors.primary,
                  }}
                >
                  {labels.createMessage}
                </Text>
              </TouchableOpacity>
            ) : null}
            {labels.createMessage && labels.searchMessage ? (
              <Text
                variant="bodyMedium"
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                {t('or')}
              </Text>
            ) : null}
            {labels.searchMessage ? (
              <TouchableOpacity onPress={onSearch}>
                <Text
                  variant="bodyMedium"
                  style={{
                    textDecorationLine: 'underline',
                    color: theme.colors.primary,
                  }}
                >
                  {labels.searchMessage}
                </Text>
              </TouchableOpacity>
            ) : null}
          </Row>
        ) : null}
      </View>
    )
  }

  return (
    <Text
      style={{
        textAlign: 'center',
        fontSize: 12,
      }}
    >
      {labels.noDataMessage}{' '}
      {labels.createMessage && (
        <TouchableOpacity onPress={onCreate}>
          <Text style={{ textDecorationLine: 'underline' }}>
            {labels.createMessage}
          </Text>
        </TouchableOpacity>
      )}
      {labels.createMessage && labels.searchMessage && (
        <>
          {' '}
          <Text style={{}}>{t('or')}</Text>{' '}
        </>
      )}
      {labels.searchMessage && (
        <>
          <TouchableOpacity onPress={onSearch}>
            <Text style={{ textDecorationLine: 'underline' }}>
              {labels.searchMessage}
            </Text>
          </TouchableOpacity>
        </>
      )}
    </Text>
  )
}
